import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "text-muted" }
const _hoisted_2 = {
  key: 0,
  class: "mt-2"
}
const _hoisted_3 = ["innerHTML"]

import { computed, nextTick, onMounted, ref, watch } from 'vue';
import {
  COMPUTE_TASK_TYPES,
  VmLifecycleType,
  VM_LIFECYCLES,
  VmLifecycleSelectOptions,
} from '@/constants';
import { highlightJson } from '@/utils/prism-utils';
import {
  BButton,
  BCollapse,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
} from 'bootstrap-vue-next';

// --- Component properties ---

export default /*@__PURE__*/_defineComponent({
  __name: 'DeveloperOptions',
  props: {
    size: {},
    computeTaskType: {}
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

// --- Component state ---
const rootEl = ref<HTMLElement | null>(null);
const size = props.size ? props.size : 'md'; // Default size if not provided

const expanded = ref(false);
const jsonParamsPreviewExpanded = ref(false);
const version = ref<string>('');
const vmLifecycle = ref<VmLifecycleType>(VM_LIFECYCLES.AUTO);
const machineType = ref<string>('');

// Compatibility values read by the Nextflow form; legacy controls are retired.
const dataPipelineMin = ref<boolean>(false);
const dataPipelineCopyData = ref<boolean>(false);

// ATTRIBUTES FROM JSON
const jsonParams = ref<string>(''); // string representation of a JSON object with additional parameters
const jsonParamsValidation = computed(() => {
  const rawValue = jsonParams.value.trim();
  if (!rawValue) {
    return {
      error: '',
      isValid: null as boolean | null,
      value: null as Record<string, unknown> | null,
    };
  }

  try {
    const parsedValue = JSON.parse(rawValue) as unknown;

    if (
      parsedValue === null ||
      Array.isArray(parsedValue) ||
      typeof parsedValue !== 'object'
    ) {
      return {
        error: 'Enter a valid JSON object.',
        isValid: false,
        value: null,
      };
    }

    return {
      error: '',
      isValid: true,
      value: parsedValue as Record<string, unknown>,
    };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error.message : 'Enter a valid JSON object.',
      isValid: false,
      value: null,
    };
  }
});

const extraAttributes = computed<Record<string, unknown> | null>(
  () => jsonParamsValidation.value.value
);
const extraAttributesInvalid = computed(
  () => jsonParamsValidation.value.isValid === false
);

const resizeJsonParamsTextarea = async () => {
  await nextTick();
  const textarea =
    rootEl.value?.querySelector<HTMLTextAreaElement>('#json-params');

  if (!textarea) {
    return;
  }

  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
};

const formatJsonParamsIfValid = async () => {
  if (!extraAttributes.value) {
    return;
  }

  const formattedValue = JSON.stringify(extraAttributes.value, null, 2);
  if (formattedValue !== jsonParams.value) {
    jsonParams.value = formattedValue;
  }

  await resizeJsonParamsTextarea();
};

watch(jsonParams, () => {
  resizeJsonParamsTextarea();
});

watch(expanded, (isExpanded) => {
  if (isExpanded) {
    resizeJsonParamsTextarea();
  }
});

// Methods to populate JSON params with sample data for easier testing
function jsonAdd_Pipeline_Optional() {
  jsonParams.value = JSON.stringify(
    {
      ...extraAttributes.value,
      startPhase: '',
      endPhase: '',
      bucketOutput: {
        name: '',
        path: '',
      },
    },
    null,
    2
  );
  resizeJsonParamsTextarea();
}

function jsonAdd_NextFlow_CustomWorkflow_Optional() {
  jsonParams.value = JSON.stringify(
    {
      ...extraAttributes.value,
      nfWorkflowSource: '',
      nfWorkflowProject: '',
      nfWorkflowRef: '',
      nfWorkflowPath: '',
      nfWorkflowEntrypoint: '',
      nfWorkflowBucket: '',
    },
    null,
    2
  );
  resizeJsonParamsTextarea();
}

function jsonAdd_NextFlow_General_Optional() {
  jsonParams.value = JSON.stringify(
    {
      ...extraAttributes.value,
      nfExecutor: '',
      nfInputBucket: '',
      nfInputPath: '',
      nfOutputBucket: '',
      nfOutputPath: '',
      nfParams: '', // string representation of a JSON object
      nfProfiles: '', // string representation of an array of strings, e.g. ["wgs_subset"]
      nfConfigFiles: '', // string representation of an array of strings, e.g. ["conf/aws.config"]
      nfResume: true,
      nfAnsiLog: true,
      nfWithTrace: true,
      nfWithReport: true,
      nfWithTimeline: true,
      nfWithDag: true,
      nfRunLabel: '',
      nfMaxRuntimeSecondsSet: false,
      nfMaxRuntimeSeconds: 1800,
      nfBucket: null, // optional developer override, e.g. "sh-nextflow-dev-us"
      nfGcpRegion: null, // optional legacy/developer override, e.g. "us-central1"
      nfAwsRegion: null, // optional legacy/developer override, e.g. "us-east-1"
      nfAwsQueue: null, // optional developer override, e.g. "arn:aws:batch:us-east-1:123456789012:job-queue/example"
      nfAwsS3WorkDir: null, // optional developer override, e.g. "s3://sh-nextflow-ml-use1/work"
    },
    null,
    2
  );
  resizeJsonParamsTextarea();
}

// MOUNTED -> AutoSize JSON Textarea
onMounted(() => {
  resizeJsonParamsTextarea();
});

// --- Exposed properties ---
__expose({
  expanded,
  version,
  vmLifecycle,
  machineType,
  // DATA PIPELINE specific options
  dataPipelineMin,
  dataPipelineCopyData,
  // Extra attributes from JSON
  extraAttributes,
  extraAttributesInvalid,
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootEl",
    ref: rootEl,
    class: _normalizeClass([expanded.value ? 'expanded-group' : 'collapsed-group'])
  }, [
    _createVNode(_unref(BButton), {
      variant: "outline-dark",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (expanded.value = !expanded.value)),
      size: _unref(size),
      class: "w-100"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_font_awesome_icon, {
          icon: ['fas', 'code'],
          class: "me-2"
        }),
        _cache[11] || (_cache[11] = _createTextVNode(" Developer Options ", -1)),
        _createVNode(_component_font_awesome_icon, {
          icon: ['fas', expanded.value ? 'angle-up' : 'angle-down'],
          class: "ms-2"
        }, null, 8, ["icon"])
      ]),
      _: 1
    }, 8, ["size"]),
    _createVNode(_unref(BCollapse), {
      modelValue: expanded.value,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((expanded).value = $event))
    }, {
      default: _withCtx(() => [
        _cache[18] || (_cache[18] = _createElementVNode("hr", null, null, -1)),
        _cache[19] || (_cache[19] = _createElementVNode("h5", { class: "mt-3" }, "General Developer Options", -1)),
        _createVNode(_unref(BFormGroup), {
          label: "VM Lifecycle",
          class: "mt-3"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormSelect), {
              options: _unref(VmLifecycleSelectOptions),
              modelValue: vmLifecycle.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((vmLifecycle).value = $event)),
              name: "vm-lifecycle",
              required: ""
            }, null, 8, ["options", "modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_unref(BFormGroup), {
          label: "Machine type",
          "label-for": "machine-type",
          class: "mt-3"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormInput), {
              modelValue: machineType.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((machineType).value = $event)),
              id: "machine-type",
              name: "machine-type",
              placeholder: "Enter machine type (e.g. f1-micro) or leave blank for default"
            }, null, 8, ["modelValue"])
          ]),
          _: 1
        }),
        _cache[20] || (_cache[20] = _createElementVNode("hr", null, null, -1)),
        _cache[21] || (_cache[21] = _createElementVNode("h5", { class: "mt-3" }, "Extra Attributes from JSON", -1)),
        _cache[22] || (_cache[22] = _createElementVNode("p", null, [
          _createElementVNode("small", { class: "text-muted" }, " Add custom extra attributes if needed. String \"null\" will be parsed as null value, strings \"true\" and \"false\" as boolean, and numbers as integers or floats. If you need to pass strictly string values, use quotes around the value, e.g. \"true\", \"123.45\" (the quotes at the beginning and end of the string will be stripped away). Hover over the type if you want to see the final value. ")
        ], -1)),
        _createVNode(_unref(BFormGroup), {
          "label-for": "nf-params",
          "invalid-feedback": jsonParamsValidation.value.error,
          state: jsonParamsValidation.value.isValid
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormTextarea), {
              modelValue: jsonParams.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((jsonParams).value = $event)),
              id: "json-params",
              name: "json-params",
              placeholder: "Enter JSON object with additional parameters, e.g. {\"param1\": \"value1\", \"param2\": 123}",
              rows: "1",
              "max-rows": "20",
              state: jsonParamsValidation.value.isValid,
              style: {"overflow":"hidden","resize":"none"},
              onBlur: formatJsonParamsIfValid,
              onInput: resizeJsonParamsTextarea
            }, null, 8, ["modelValue", "state"]),
            _createElementVNode("small", _hoisted_1, [
              _cache[13] || (_cache[13] = _createTextVNode(" Valid JSON objects are automatically formatted with 2-space indentation. ", -1)),
              (extraAttributes.value)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    class: "text-muted",
                    style: {"cursor":"pointer"},
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (jsonParamsPreviewExpanded.value = !jsonParamsPreviewExpanded.value))
                  }, [
                    _cache[12] || (_cache[12] = _createTextVNode(" Preview ", -1)),
                    _createVNode(_component_font_awesome_icon, {
                      icon: [
                'fas',
                jsonParamsPreviewExpanded.value ? 'angle-up' : 'angle-down',
              ],
                      class: "ms-1"
                    }, null, 8, ["icon"])
                  ]))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }, 8, ["invalid-feedback", "state"]),
        (extraAttributes.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createVNode(_unref(BCollapse), {
                modelValue: jsonParamsPreviewExpanded.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((jsonParamsPreviewExpanded).value = $event))
              }, {
                default: _withCtx(() => [
                  _createElementVNode("pre", {
                    class: "code-highlight mb-0",
                    innerHTML: _unref(highlightJson)(extraAttributes.value)
                  }, null, 8, _hoisted_3)
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", null, [
          (props.computeTaskType === _unref(COMPUTE_TASK_TYPES).NEXTFLOW_PIPELINE)
            ? (_openBlock(), _createBlock(_unref(BButton), {
                key: 0,
                variant: "dark",
                class: "me-2 mt-1",
                size: _unref(size),
                onClick: _cache[6] || (_cache[6] = ($event: any) => (jsonAdd_Pipeline_Optional()))
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_font_awesome_icon, {
                    icon: ['fas', 'plus'],
                    class: "me-1"
                  }),
                  _cache[14] || (_cache[14] = _createTextVNode(" Pipeline Optional Attributes ", -1))
                ]),
                _: 1
              }, 8, ["size"]))
            : _createCommentVNode("", true),
          (props.computeTaskType === _unref(COMPUTE_TASK_TYPES).NEXTFLOW_PIPELINE)
            ? (_openBlock(), _createBlock(_unref(BButton), {
                key: 1,
                variant: "dark",
                class: "me-2 mt-1",
                size: _unref(size),
                onClick: _cache[7] || (_cache[7] = ($event: any) => (jsonAdd_NextFlow_CustomWorkflow_Optional()))
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_font_awesome_icon, {
                    icon: ['fas', 'plus'],
                    class: "me-1"
                  }),
                  _cache[15] || (_cache[15] = _createTextVNode(" Custom NextFlow Workflow ", -1))
                ]),
                _: 1
              }, 8, ["size"]))
            : _createCommentVNode("", true),
          (props.computeTaskType === _unref(COMPUTE_TASK_TYPES).NEXTFLOW_PIPELINE)
            ? (_openBlock(), _createBlock(_unref(BButton), {
                key: 2,
                variant: "dark",
                class: "me-2 mt-1",
                size: _unref(size),
                onClick: _cache[8] || (_cache[8] = ($event: any) => (jsonAdd_NextFlow_General_Optional()))
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_font_awesome_icon, {
                    icon: ['fas', 'plus'],
                    class: "me-1"
                  }),
                  _cache[16] || (_cache[16] = _createTextVNode(" NextFlow Optional Attributes ", -1))
                ]),
                _: 1
              }, 8, ["size"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(BButton), {
            variant: "danger",
            class: "me-2 mt-1",
            size: _unref(size),
            onClick: _cache[9] || (_cache[9] = ($event: any) => {
            jsonParams.value = '';
            extraAttributes.value = null;
            resizeJsonParamsTextarea();
          })
          }, {
            default: _withCtx(() => [
              _createVNode(_component_font_awesome_icon, {
                icon: ['fas', 'trash'],
                class: "me-1"
              }),
              _cache[17] || (_cache[17] = _createTextVNode(" Clear JSON attributes ", -1))
            ]),
            _: 1
          }, 8, ["size"])
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 2))
}
}

})